import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageCircle, 
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { contactInfo } from '../lib/data';
import { toast } from 'sonner';
import PageMeta from '../components/PageMeta';

const serviceOptions = [
  'Injury & Sports Treatments',
  'Nutritional Therapy',
  'Once Off Services',
  'Talks & Workshops',
  'General Enquiry',
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service_interest: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const contactMethods = [
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'The quickest way to reach Ali',
      value: 'Message on WhatsApp',
      link: contactInfo.whatsapp,
      isExternal: true,
      highlight: true,
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'For detailed enquiries',
      value: contactInfo.email,
      link: `mailto:${contactInfo.email}`,
      isExternal: false,
    },
    {
      icon: Phone,
      title: 'Phone',
      description: 'Call directly',
      value: contactInfo.phone,
      link: `tel:${contactInfo.phone.replace(/\s/g, '')}`,
      isExternal: false,
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '', service_interest: '' });
        toast.success('Message sent successfully!');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageMeta
        title="Contact Ali Dorgan | Cobh, Cork"
        description="Get in touch with Ali Dorgan for neuromuscular therapy and nutritional support in Cobh, Cork. Send a message, call, or reach out on WhatsApp."
      />
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
              Contact
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal leading-tight mb-8">
              Get in Touch
            </h1>
            <p className="text-xl text-charcoal/70 leading-relaxed">
              Have a question or ready to book an appointment? Send a message below or 
              reach out directly. WhatsApp is the quickest way to get a response.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {contactMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full border-0 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-300 rounded-2xl ${
                  method.highlight ? 'bg-sage-50' : 'bg-cream-50'
                }`}>
                  <CardContent className="p-10 text-center">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-8 ${
                      method.highlight ? 'bg-[#25D366]' : 'bg-sage-100'
                    }`}>
                      <method.icon className={`w-8 h-8 ${
                        method.highlight ? 'text-white' : 'text-sage-600'
                      }`} />
                    </div>
                    <h3 className="font-serif text-2xl font-medium text-charcoal mb-3">
                      {method.title}
                    </h3>
                    <p className="text-charcoal/60 text-base mb-6">
                      {method.description}
                    </p>
                    <a
                      href={method.link}
                      target={method.isExternal ? '_blank' : undefined}
                      rel={method.isExternal ? 'noopener noreferrer' : undefined}
                      className={`inline-block font-semibold transition-colors ${
                        method.highlight 
                          ? 'text-[#25D366] hover:text-[#20BD5C]' 
                          : 'text-sage-600 hover:text-sage-700'
                      }`}
                      data-testid={`contact-${method.title.toLowerCase()}`}
                    >
                      {method.value}
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 md:py-28 bg-cream-50">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-4 block">
                Send a Message
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-charcoal">
                Contact Form
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white border-0 shadow-soft rounded-2xl">
                <CardContent className="p-8 md:p-10">
                  {submitted ? (
                    <div className="text-center py-8" data-testid="contact-form-success">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                      <h3 className="font-serif text-2xl font-medium text-charcoal mb-3">
                        Message Sent
                      </h3>
                      <p className="text-charcoal/60 mb-8">
                        Thank you for reaching out. Ali will get back to you shortly.
                      </p>
                      <Button
                        onClick={() => setSubmitted(false)}
                        variant="outline"
                        className="rounded-full px-8 border-charcoal text-charcoal hover:bg-charcoal hover:text-white"
                        data-testid="contact-form-reset"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-charcoal mb-2">
                            Name <span className="text-red-400">*</span>
                          </label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Your name"
                            className="h-12 bg-cream-50 border-border/50 rounded-xl"
                            required
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-charcoal mb-2">
                            Email <span className="text-red-400">*</span>
                          </label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="your@email.com"
                            className="h-12 bg-cream-50 border-border/50 rounded-xl"
                            required
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-charcoal mb-2">
                            Phone
                          </label>
                          <Input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="+353 ..."
                            className="h-12 bg-cream-50 border-border/50 rounded-xl"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-charcoal mb-2">
                            Service Interest
                          </label>
                          <Select
                            value={formData.service_interest}
                            onValueChange={(value) => setFormData({ ...formData, service_interest: value })}
                          >
                            <SelectTrigger className="h-12 bg-cream-50 border-border/50 rounded-xl" data-testid="contact-service-select">
                              <SelectValue placeholder="Select a service..." />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceOptions.map((service) => (
                                <SelectItem key={service} value={service}>{service}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-2">
                          Message <span className="text-red-400">*</span>
                        </label>
                        <Textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Tell us about your enquiry..."
                          rows={5}
                          className="bg-cream-50 border-border/50 rounded-xl resize-none"
                          required
                          data-testid="contact-message-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        size="lg"
                        className="w-full bg-charcoal hover:bg-charcoal/90 text-white rounded-xl h-12 gap-2 font-semibold"
                        data-testid="contact-submit-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-20 md:py-28 bg-sage-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-16 max-w-5xl mx-auto">
            {/* Location */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-sage-600" />
                </div>
                <h2 className="font-serif text-3xl font-light text-charcoal">Location</h2>
              </div>
              <p className="text-charcoal/70 text-lg mb-6">
                {contactInfo.location}
              </p>
              {/* Google Maps embed */}
              <div className="rounded-2xl overflow-hidden shadow-soft" data-testid="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2471.5!2d-8.297!3d51.851!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4844a1c1e7b7a8e1%3A0x506dcd4a9a0c3f0!2sP24+PX86%2C+Cobh%2C+Co.+Cork!5e0!3m2!1sen!2sie!4v1"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ali Dorgan Clinic Location - Cobh, Cork"
                  className="w-full"
                />
              </div>
            </motion.div>

            {/* Hours */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center">
                  <Clock className="w-7 h-7 text-sage-600" />
                </div>
                <h2 className="font-serif text-3xl font-light text-charcoal">Availability</h2>
              </div>
              <p className="text-charcoal/70 text-lg mb-8 leading-relaxed">
                Appointments are available by arrangement. Please get in touch to discuss 
                availability and book a suitable time.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-10 py-6 gap-2 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                data-testid="contact-whatsapp-btn"
              >
                <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5" />
                  Check Availability
                </a>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-charcoal">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-light text-cream-100 mb-8">
              Ready to Book?
            </h2>
            <p className="text-cream-200/80 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
              View available services and book your appointment.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white text-charcoal hover:bg-cream-100 rounded-full px-10 py-6 font-semibold hover:-translate-y-0.5 transition-all duration-300"
              data-testid="contact-book-btn"
            >
              <a href="/services/injury-sports#practitioners">
                Book Injury & Sports Treatment
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
